import { useEffect } from 'react';
import type { ReactNode } from 'react';

import usePageTranslation from '../../../hooks/ui/usePageTranslation';
import { getTranslatedTextOrDefault } from '../../../util/util';

import { useMobilePreview } from '../hooks/use-mobile-preview';
import styles from './mobile-preview-shell.module.scss';

interface MobilePreviewShellProps {
  children: ReactNode;
}

const HTML_PREVIEW_CLASS = 'bc-mobile-preview-active';

export function MobilePreviewShell({ children }: MobilePreviewShellProps) {
  const { isMobilePreview, disable } = useMobilePreview();
  const PageTranslation = usePageTranslation();
  const translations = Array.isArray(PageTranslation) ? PageTranslation : [];
  const stageLabel = getTranslatedTextOrDefault(
    'mobilePreview.stage.label',
    translations,
    'Mobile preview of the current page',
  );

  useEffect(() => {
    document.documentElement.classList.toggle(HTML_PREVIEW_CLASS, isMobilePreview);
    return () => {
      document.documentElement.classList.remove(HTML_PREVIEW_CLASS);
    };
  }, [isMobilePreview]);

  useEffect(() => {
    if (!isMobilePreview) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        disable();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isMobilePreview, disable]);

  return (
    <div className={isMobilePreview ? styles.root : undefined}>
      <div
        className={isMobilePreview ? `${styles.stage} bc-mobile-preview-stage` : styles.host}
        {...(isMobilePreview
          ? { role: 'region', 'aria-label': stageLabel, 'data-testid': 'mobile-preview-stage' }
          : {})}
      >
        <div className={isMobilePreview ? `${styles.viewport} bc-mobile-preview-viewport` : undefined}>
          {children}
        </div>
      </div>
    </div>
  );
}
